// React.
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

// Third-parties.
import { Column, usePagination, useSortBy, useTable } from 'react-table';

// Types.
import InstanceTarget from '../../../types/instance/InstanceTarget';
import { OracleSqlHashWithMetric } from "../types";

// Helper.
import Helper from '../../../helpers/Helper';
import { getDefaultPageSize, getToolTipPosition, stringFormatting } from '../../../helpers/utils';
import { showError } from '../../../utils/showAlert';

// Components.
import Alert from "../../../component/Alert";
import ReactTable from "../../../component/ReactTable";
import StatementToolTip from "../../../component/instance/table/StatementToolTip";
import TableCSVExport from "../../../component/TableCSVExport";

// Constants.
import { DEFAULT_TABLE_METRICS, } from "../constants";
import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { buildRedirectQuery } from "../../../helpers/timeRangeUtils";
import SearchInput from '../../../component/SearchInput';

export default function OracleStatisticsTable(props: {
                                                  instance: InstanceTarget,
                                                  generateId: number,
                                                  aggregate: number,
                                                  chart: string,
                                                  filter: string,
                                                  metric: string, tagValue: string,
                                                  tagName: string, topN: number,
                                                  shwSqlID: boolean,
                                                  statements: OracleSqlHashWithMetric[],
                                                  customSqlId: string,
                                              }
) {
    const timeRangeContext = useContext(TimeRangeContext);

    const [sqlStatistics, setSqlStatistics] = useState<OracleSqlHashWithMetric[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [visibleToolTip, setVisibleToolTip] = useState({
        id: '',
        position: 0
    });

    const navigate = useNavigate();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const [tableFilter, setTableFilter] = useState(query.get("filter") || "");
    const initialPageIndex = Number(query.get("page") || 0);
    const initialPageSize = Number(query.get("pageSize") || getDefaultPageSize());

    const initialColumns = useMemo(() => {
        if (props.tagName !== 'none' && !props.tagValue) {
            return [{
                Header: 'Tag Value',
                accessor: 'tagValue',
                headerClassName: 'text',
                className: 'concatenate',
                canSort: true,
                Cell: ({cell}: any ) =>
                    <React.Fragment>
                        <span className="me-1 ps-1"
                              style={{backgroundColor: cell.row.original.color}}>&nbsp;</span>
                        {props.customSqlId ?
                            <Link target="_blank"
                                  to={`/reports/sql-stats-explorer?instance=${props.instance.id}&${buildRedirectQuery(timeRangeContext.timeRange)}&filter=${props.customSqlId}&metric=${props.metric}&chart=${props.chart}&showSqlId=${props.shwSqlID}&customId=${props.shwSqlID ? cell.row.original.sqlid : cell.row.original.sqlhash}`}
                                  className='mr-1 open_statement'>{cell.row.original.tagvalue}</Link>
                            :
                            <Link target="_blank"
                                  to={`/reports/sql-stats-explorer?instance=${props.instance.id}&${buildRedirectQuery(timeRangeContext.timeRange)}&metric=${props.metric}&chart=${props.chart}&showSqlId=${props.shwSqlID}&tagValue=${cell.row.original.tagvalue}&tagName=${props.tagName}`}>{cell.row.original.tagvalue}</Link>}
                    </React.Fragment>
            }]
        } else {
            return [{
                Header: '#',
                accessor: 'sqlhash',
                headerClassName: 'text',
                className: 'concatenate',
                canSort: true,
                Cell: ({cell}: any) =>
                    <React.Fragment>
                        <span className="me-1 ps-1"
                              style={{backgroundColor: cell.row.original.color}}>&nbsp;</span>
                        {(!props.filter || props.tagValue) && <Link target="_blank"
                                                                    to={`/reports/sql-stats-explorer?instance=${props.instance.id}&${buildRedirectQuery(timeRangeContext.timeRange)}&filter=${cell.row.values.sqlhash}&metric=${props.metric}&chart=${props.chart}&showSqlId=${props.shwSqlID}&customId=${props.shwSqlID ? cell.row.original.sqlid : cell.row.original.sqlhash}`}
                                                                    className='mr-1 open_statement'>
                            <i className={`fal fa-light fa-arrow-up-right-from-square fa-fw`}
                               data-tooltip-id="global-tooltip" data-tooltip-content="Open statement in new tab"/>
                        </Link>}
                        <Link target="_blank"
                              to={`/instances/${props.instance.id}/activity/statement/${cell.row.values.sqlhash}/sql-statistics?${buildRedirectQuery(timeRangeContext.timeRange)}`}>{props.shwSqlID && cell.row.original.sqlid ? cell.row.original.sqlid : cell.row.values.sqlhash}</Link>
                    </React.Fragment>
            },
                {
                    Header: 'Statement',
                    accessor: 'sqltext',
                    headerClassName: 'text',
                    className: 'concatenate-large',
                    canSort: true,
                    Cell: ({cell}: any) => <Link
                        to={`/instances/${props.instance.id}/activity/statement/${cell.row.values.sqlhash}/sql-statistics?${buildRedirectQuery(timeRangeContext.timeRange)}`}>{cell.row.values.sqltext.substring(0, 200)}</Link>
                }
            ]
        }
    }, [sqlStatistics])

    // Table columns
    const columns: Column[] = useMemo(() => {
        let secondSortOption = props.tagName !== 'none' ? 'tagvalue' : 'sqlhash'
        let defaultColumns = [
            ...initialColumns,
            {
                Header: 'Duration',
                accessor: DEFAULT_TABLE_METRICS.DURATION,
                headerClassName: 'concatenate-medium text-end',
                className: 'concatenate-medium text-end',
                canSort: true,
                sortType: (rowA: any, rowB: any) => {
                    return rowA.values[DEFAULT_TABLE_METRICS.DURATION] - rowB.values[DEFAULT_TABLE_METRICS.DURATION] || rowA.original[secondSortOption]?.localeCompare(rowB.original[secondSortOption])
                },
                sortDescFirst: true,
                // @ts-ignore
                Cell: ({cell}) =>
                    (Helper.getTimeInEnglish(cell.row.values.duration))

            },
            {
                Header: 'Executions',
                accessor: DEFAULT_TABLE_METRICS.EXECUTIONS,
                headerClassName: 'concatenate-medium text-end',
                className: 'concatenate-medium text-end',
                canSort: true,
                sortType: (rowA: any, rowB: any) => {
                    return rowA.values[DEFAULT_TABLE_METRICS.EXECUTIONS] - rowB.values[DEFAULT_TABLE_METRICS.EXECUTIONS] || rowA.original[secondSortOption]?.localeCompare(rowB.original[secondSortOption])
                },
                sortDescFirst: true,
                // @ts-ignore
                Cell: ({cell}) =>
                    (cell.row.values.executions ? Helper.getFormattedNumber(cell.row.values.executions) : '')
            },
            {
                Header: 'Avg. Duration',
                accessor: 'average_duration',
                headerClassName: 'concatenate-medium text-end',
                className: 'concatenate-medium text-end',
                canSort: true,
                sortType: 'basic',
                sortDescFirst: true,
                // @ts-ignore
                Cell: ({cell}) => cell.row.values.average_duration ? Helper.getTimeInEnglish(cell.row.values.average_duration) : ''
            },
            {
                Header: 'CPU Time',
                accessor: DEFAULT_TABLE_METRICS.CPU_TIME_MILLISECONDS,
                headerClassName: 'concatenate-medium text-end',
                className: 'concatenate-medium text-end',
                canSort: true,
                sortDescFirst: true,
                sortType: (rowA: any, rowB: any) => {
                    return rowA.values[DEFAULT_TABLE_METRICS.CPU_TIME_MILLISECONDS] - rowB.values[DEFAULT_TABLE_METRICS.CPU_TIME_MILLISECONDS] || rowA.original[secondSortOption]?.localeCompare(rowB.original[secondSortOption])
                },
                // @ts-ignore
                Cell: ({cell}) =>
                    (cell.row.values.cpu_time_milliseconds ? Helper.getTimeInEnglish(cell.row.values.cpu_time_milliseconds) : '')
            },
            {
                Header: 'Physical Reads',
                accessor: DEFAULT_TABLE_METRICS.PHYSICAL_READS,
                headerClassName: 'concatenate-medium text-end',
                className: 'concatenate-medium text-end',
                canSort: true,
                sortType: (rowA: any, rowB: any) => {
                    return rowA.values[DEFAULT_TABLE_METRICS.PHYSICAL_READS] - rowB.values[DEFAULT_TABLE_METRICS.PHYSICAL_READS] || rowA.original[secondSortOption]?.localeCompare(rowB.original[secondSortOption])
                },
                sortDescFirst: true,
                // @ts-ignore
                Cell: ({cell}) =>
                    (cell.row.values.physical_reads ? Helper.getFormattedNumber(cell.row.values.physical_reads) : '')
            },
            {
                Header: 'Logical Reads',
                accessor: DEFAULT_TABLE_METRICS.LOGICAL_READS,
                headerClassName: 'concatenate-medium text-end',
                className: 'concatenate-medium text-end',
                canSort: true,
                sortType: (rowA: any, rowB: any) => {
                    return rowA.values[DEFAULT_TABLE_METRICS.LOGICAL_READS] - rowB.values[DEFAULT_TABLE_METRICS.LOGICAL_READS] || rowA.original[secondSortOption]?.localeCompare(rowB.original[secondSortOption])
                },
                sortDescFirst: true,
                // @ts-ignore
                Cell: ({cell}) =>
                    (cell.row.values.logical_reads ? Helper.getFormattedNumber(cell.row.values.logical_reads) : '')
            },
            {
                Header: 'Logical Writes',
                accessor: DEFAULT_TABLE_METRICS.LOGICAL_WRITES,
                headerClassName: 'concatenate-medium col-width-4 text-end',
                className: 'concatenate-medium text-end',
                canSort: true,
                sortType: (rowA: any, rowB: any) => {
                    return rowA.values[DEFAULT_TABLE_METRICS.LOGICAL_WRITES] - rowB.values[DEFAULT_TABLE_METRICS.LOGICAL_WRITES] || rowA.original[secondSortOption]?.localeCompare(rowB.original[secondSortOption])
                },
                sortDescFirst: true,
                // @ts-ignore
                Cell: ({cell}) =>
                    (cell.row.values.logical_writes ? Helper.getFormattedNumber(cell.row.values.logical_writes) : '')
            }
        ]

        const doesSelectedMetricExist = Object.keys(DEFAULT_TABLE_METRICS).indexOf(props.metric.toUpperCase())
        if (doesSelectedMetricExist < 0 && props.metric) {
            defaultColumns.push({
                Header: stringFormatting(props.metric).replace(/_/gi, ' '),
                accessor: props.metric,
                headerClassName: 'concatenate-medium col-width-4 text-end',
                className: 'concatenate-medium text-end',
                canSort: true,
                sortDescFirst: true,
                sortType: (rowA: any, rowB: any) => {
                    return rowA.values[props.metric] - rowB.values[props.metric] || rowA.values[secondSortOption]?.localeCompare(rowB.original[secondSortOption])
                },
                // @ts-ignore
                Cell: ({cell}) => {
                    return props.metric.includes('time') ? Helper.getTimeInEnglish(cell.row.values[props.metric]) : `${Helper.getFormattedNumber(cell.row.values[props.metric], props.metric)}${Helper.getMetricFormatString(props.metric, false)}`
                }
            })
        }
        return defaultColumns
    }, [sqlStatistics]);

    // Table data
    useEffect(() => {
            async function load() {
                try {
                    setLoading(true);
                    const results = props.statements
                    const resultsWithAverage = Helper.addAverageAndColor('duration', 'executions', results, props.topN)
                    if (active) {
                        setLoading(false);
                        setSqlStatistics(resultsWithAverage);
                    }
                } catch (x: any) {
                    const errorMessage = x.message || x.toString();
                    setError(errorMessage);
                    showError('Failed to load Oracle statistics', errorMessage);
                    setLoading(false);
                    setSqlStatistics([]);
                }
            }

            let active: boolean = true;
            load();
            return () => {
                active = false;
            }
        },
        [timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval, props.instance.id, props.statements, props.aggregate, props.filter, props.instance.id, props.metric, props.tagName, props.shwSqlID, props.tagValue]);

    const data = useMemo(() => {
        const filteredData = sqlStatistics.filter((row) =>
            row.sqltext?.toLowerCase().includes(tableFilter.toLowerCase()) ||
            row.sqlid?.toString().toLowerCase().includes(tableFilter.toLowerCase()) ||
            row.sqlhash?.toLowerCase().includes(tableFilter.toLowerCase()) ||
            row.tagvalue?.toLowerCase().includes(tableFilter.toLowerCase()))

        filteredData.forEach((row) => {
            row.sqltext = row.sqltext?.replace(/"/gi, '\'') || ''
        })

        return filteredData
    }, [sqlStatistics, tableFilter]);


    const tableTitle = useMemo(() => {
        if (props.filter) {
            return props.statements.length ? `Statements ${`for # ${props.statements[0].sqlhash}, ID ${props.statements[0].sqlid}`}` : ''
        }
        if (props.tagValue) {
            return `Statements for Tag Name: ${props.tagName}, Tag Value: ${props.tagValue}`
        }
        return `Statements `
    }, [props.generateId, props.statements])

    const tableInstance = useTable(
            {
                columns,
                data,
                initialState:
                    {
                        pageIndex: initialPageIndex,
                        pageSize: initialPageSize,
                        sortBy: [{id: props.metric, desc: true}],
                    },
                disableSortRemove: true
            }, useSortBy, usePagination);

    const
        {
            getTableProps,
            getTableBodyProps,
            headerGroups,
            prepareRow,
            page,
            setPageSize,
            pageOptions,
            pageCount,
            canPreviousPage,
            previousPage,
            canNextPage,
            nextPage,
            gotoPage,
            state: {pageIndex, pageSize}
        } = tableInstance;

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        if (tableFilter) {
            params.set('filter', tableFilter);
        } else {
            params.delete('filter');
        }

        params.set('page', pageIndex.toString());
        params.set('pageSize', pageSize.toString());

        navigate({ search: params.toString() }, { replace: true });
    }, [tableFilter, pageIndex, pageSize, location.search]);

    useEffect(() => {
        if (tableFilter) {
            gotoPage(0);
        }
    }, [tableFilter]);

    function clearTableFilter() {
        setTableFilter('');
    }

    return (
        <div className="card">
            <div className="card-header">
                {tableTitle}
                <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                   data-bs-target="#collapseStatements" aria-expanded="false" aria-controls="collapseStatements"/>
            </div>
            <div id="collapseStatements" className="card-body collapse show">
                {Number(process.env.REACT_APP_API_LIMIT) - 1 === data.length - 1 &&
                    (<Alert
                        message={`To help preserve performance, we limit the total number of statement records below to the top ${Number(process.env.REACT_APP_API_LIMIT) - 1} in descending order by the number of executions.`}
                        heading="Statement records" variant="alert-info"/>)}
                <div className="row row-cols-1 row-cols-md-2 table-search">
                    <div className="col col-md-10">
                        <TableCSVExport
                            tableInstance={tableInstance}
                            filename="SQL Statistics"
                        />
                    </div>
                    <div className="col col-md-2">
                        <SearchInput
                            value={tableFilter}
                            onChange={(e) => setTableFilter(e.target.value)}
                            onClear={clearTableFilter}
                        />
                    </div>
                </div>
                <ReactTable
                    getTableProps={getTableProps}
                    getTableBodyProps={getTableBodyProps}
                    headerGroups={headerGroups}
                    prepareRow={prepareRow}
                    page={page}
                    canPreviousPage={canPreviousPage}
                    canNextPage={canNextPage}
                    pageOptions={pageOptions}
                    pageCount={pageCount}
                    gotoPage={gotoPage}
                    nextPage={nextPage}
                    previousPage={previousPage}
                    setPageSize={setPageSize}
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    dataLength={data.length}
                    error={error}
                    loading={loading}
                    renderCell={(cell) => {
                        if (cell.column.id === "sqltext") {
                            return (
                                <div className='tooltip-scroll ellipsis'
                                    onMouseEnter={(event) => setVisibleToolTip({
                                        id: cell.row.values.sqlhash,
                                        position: getToolTipPosition(event)
                                    })}
                                    onMouseLeave={() => setVisibleToolTip({ id: '', position: 0 })}
                                >
                                    {cell.render('Cell')}
                                    {cell.row.values.sqlhash === visibleToolTip.id && (
                                        <StatementToolTip instanceType={props.instance.type}
                                            position={visibleToolTip.position}
                                            id={cell.row.values.sqlhash}
                                            link={`/instances/${props.instance.id}/activity/statement/${cell.row.values.sqlhash}/text`}
                                            cell={cell} />
                                    )}
                                </div>
                            );
                        }
                        return cell.render('Cell');
                    }}
                />
            </div>
        </div>
    )
}
